// src/services/verificacaoService.ts
// Tipos e funções para o portal de verificação por protocolo
// CREATED: 2026-05-07 — v1.2

import api from './api';

// ─── Tipos ────────────────────────────────────────────────────

export type VerificacaoStatus =
  | 'pending'
  | 'under_review'
  | 'payment_pending'
  | 'payment_confirmed'
  | 'active'
  | 'inactive'
  | 'suspended'
  | 'rejected';

export interface DocumentoVerificacao {
  id:           string;
  fileName:     string;
  url:          string;
  mimeType:     string;
  fileSize:     number;
  documentType: string;
  description:  string;
  createdAt:    string;
}

export interface VerificacaoData {
  id:                   string;
  systemId:             string;
  protocolo:            string;
  status:               VerificacaoStatus;
  schoolName:           string;
  responsibleName:      string | null;
  email:                string | null;
  phone:                string | null;
  whatsapp:             string | null;
  city:                 string | null;
  state:                string | null;
  dataNascimento:       string | null;
  createdAt:            string;
  updatedAt:            string;
  paymentLink:          string | null;
  paymentValue:         number | null;
  paymentConfirmedAt:   string | null;
  accessVideosReleased: boolean;
  accessLogin:          string | null;
  accessPassword:       string | null;
  documentos:           DocumentoVerificacao[];
  metadata:             Record<string, any>;
}

export interface VerificacaoError {
  type:    string;
  code:    string;
  message: string;
}

export interface ConsultaResult {
  data:  VerificacaoData | null;
  error: VerificacaoError | null;
}

// ─── Config de status ─────────────────────────────────────────

export const STATUS_CONFIG: Record<VerificacaoStatus, {
  label:       string;
  description: string;
  color:       string;
  bg:          string;
  step:        number;
}> = {
  pending: {
    label:       'Aguardando Análise',
    description: 'Recebemos seu cadastro. Nossa equipe irá analisá-lo em breve.',
    color:       '#d97706',
    bg:          '#fef3c7',
    step:        1,
  },
  under_review: {
    label:       'Em Análise',
    description: 'Sua solicitação está sendo analisada pela nossa equipe.',
    color:       '#2563eb',
    bg:          '#dbeafe',
    step:        2,
  },
  payment_pending: {
    label:       'Aprovado — Aguardando Pagamento',
    description: 'Parabéns! Sua solicitação foi aprovada. Realize o pagamento abaixo para ativar sua filiação.',
    color:       '#7c3aed',
    bg:          '#f5f3ff',
    step:        3,
  },
  payment_confirmed: {
    label:       'Pagamento Confirmado',
    description: 'Pagamento confirmado! Estamos finalizando a ativação do seu acesso.',
    color:       '#059669',
    bg:          '#d1fae5',
    step:        3,
  },
  active: {
    label:       'Filiação Ativa',
    description: 'Sua filiação está ativa. Acesse o portal com as credenciais abaixo.',
    color:       '#065f46',
    bg:          '#d1fae5',
    step:        4,
  },
  inactive: {
    label:       'Filiação Inativa',
    description: 'Sua filiação está temporariamente inativa. Entre em contato com o suporte.',
    color:       '#6b7280',
    bg:          '#f3f4f6',
    step:        0,
  },
  suspended: {
    label:       'Filiação Suspensa',
    description: 'Sua filiação foi suspensa. Entre em contato com o suporte.',
    color:       '#dc2626',
    bg:          '#fef2f2',
    step:        0,
  },
  rejected: {
    label:       'Solicitação Recusada',
    description: 'Infelizmente sua solicitação não foi aprovada. Entre em contato para mais informações.',
    color:       '#dc2626',
    bg:          '#fef2f2',
    step:        0,
  },
};

// ─── Helpers ──────────────────────────────────────────────────

export const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

export const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch { return '—'; }
};

// ─── Service ──────────────────────────────────────────────────

export const verificacaoService = {
  async consultarProtocolo(protocolo: string): Promise<ConsultaResult> {
    const trimmed = protocolo.trim();

    if (!trimmed) {
      return {
        data: null,
        error: { type: 'unknown', code: 'EMPTY', message: 'Informe o número do protocolo.' },
      };
    }

    try {
      // FIX: api.get<T> retorna T diretamente (não axios), então não existe .data
      const response = await api.get<VerificacaoData>(
        `/affiliates/public/protocolo/${encodeURIComponent(trimmed)}`
      );
      return { data: response, error: null };
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        return {
          data: null,
          error: { type: 'not_found', code: 'NOT_FOUND', message: 'Protocolo não encontrado. Verifique se o número está correto.' },
        };
      }
      if (status === 400) {
        return {
          data: null,
          error: { type: 'invalid', code: 'INVALID', message: 'Formato de protocolo inválido.' },
        };
      }
      return {
        data: null,
        error: { type: 'server_error', code: 'SERVER_ERROR', message: 'Erro ao consultar o protocolo. Tente novamente em instantes.' },
      };
    }
  },
};

// Named export — usado pelo PortalVerificacao e VerificacaoProtocolo
export const consultarProtocolo = (protocolo: string): Promise<ConsultaResult> =>
  verificacaoService.consultarProtocolo(protocolo);

export default verificacaoService;